/**
 * Telegram WebApp utilities
 * Integration with Telegram Mini App SDK
 */

interface TelegramWebApp {
  initData: string;
  initDataUnsafe: {
    user?: {
      id: number;
      first_name: string;
      last_name?: string;
      username?: string;
      language_code?: string;
    };
    query_id?: string;
    auth_date?: number;
    hash?: string;
  };
  version: string;
  platform: string;
  colorScheme: 'light' | 'dark';
  themeParams: {
    bg_color?: string;
    text_color?: string;
    hint_color?: string;
    link_color?: string;
    button_color?: string;
    button_text_color?: string;
  };
  isExpanded: boolean;
  viewportHeight: number;
  viewportStableHeight: number;
  headerColor: string;
  backgroundColor: string;
  BackButton: {
    isVisible: boolean;
    onClick: (callback: () => void) => void;
    offClick: (callback: () => void) => void;
    show: () => void;
    hide: () => void;
  };
  MainButton: {
    text: string;
    color: string;
    textColor: string;
    isVisible: boolean;
    isActive: boolean;
    isProgressVisible: boolean;
    setText: (text: string) => void;
    onClick: (callback: () => void) => void;
    offClick: (callback: () => void) => void;
    show: () => void;
    hide: () => void;
    enable: () => void;
    disable: () => void;
    showProgress: (leaveActive?: boolean) => void;
    hideProgress: () => void;
    setParams: (params: {
      text?: string;
      color?: string;
      text_color?: string;
      is_active?: boolean;
      is_visible?: boolean;
    }) => void;
  };
  HapticFeedback: {
    impactOccurred: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void;
    notificationOccurred: (type: 'error' | 'success' | 'warning') => void;
    selectionChanged: () => void;
  };
  ready: () => void;
  expand: () => void;
  close: () => void;
  enableClosingConfirmation: () => void;
  disableClosingConfirmation: () => void;
  onEvent: (eventType: string, callback: () => void) => void;
  offEvent: (eventType: string, callback: () => void) => void;
  sendData: (data: string) => void;
  openLink: (url: string, options?: { try_instant_view?: boolean }) => void;
  openTelegramLink: (url: string) => void;
  openInvoice: (url: string, callback?: (status: string) => void) => void;
  showPopup: (params: {
    title?: string;
    message: string;
    buttons?: Array<{
      id?: string;
      type?: 'default' | 'ok' | 'close' | 'cancel' | 'destructive';
      text?: string;
    }>;
  }, callback?: (buttonId: string) => void) => void;
  showAlert: (message: string, callback?: () => void) => void;
  showConfirm: (message: string, callback?: (confirmed: boolean) => void) => void;
  setHeaderColor: (color: string) => void;
  setBackgroundColor: (color: string) => void;
}

/**
 * Get Telegram WebApp instance
 */
export const getTelegramWebApp = (): any | null => {
  if (typeof window !== 'undefined' && (window as any).Telegram?.WebApp) {
    return (window as any).Telegram.WebApp;
  }
  return null;
};

/**
 * Initialize Telegram WebApp
 */
export const initTelegramWebApp = (): any => {
  const tg = getTelegramWebApp();

  if (!tg) {
    // Fallback for development/testing
    console.warn('Telegram WebApp not available - using mock');
    return createMockWebApp();
  }

  return tg;
};

/**
 * Create mock WebApp for development
 */
const createMockWebApp = (): TelegramWebApp => {
  return {
    initData: '',
    initDataUnsafe: {
      user: {
        id: 123456789,
        first_name: 'Test',
        last_name: 'User',
        username: 'testuser',
        language_code: 'en'
      }
    },
    version: '6.0',
    platform: 'web',
    colorScheme: 'dark',
    themeParams: {
      bg_color: '#1C1C1E',
      text_color: '#FFFFFF',
      button_color: '#4CAF50'
    },
    isExpanded: true,
    viewportHeight: 600,
    viewportStableHeight: 600,
    headerColor: '#1C1C1E',
    backgroundColor: '#000000',
    BackButton: {
      isVisible: false,
      onClick: () => {},
      offClick: () => {},
      show: () => {},
      hide: () => {}
    },
    MainButton: {
      text: '',
      color: '#4CAF50',
      textColor: '#FFFFFF',
      isVisible: false,
      isActive: true,
      isProgressVisible: false,
      setText: () => {},
      onClick: () => {},
      offClick: () => {},
      show: () => {},
      hide: () => {},
      enable: () => {},
      disable: () => {},
      showProgress: () => {},
      hideProgress: () => {},
      setParams: () => {}
    },
    HapticFeedback: {
      impactOccurred: () => {},
      notificationOccurred: () => {},
      selectionChanged: () => {}
    },
    ready: () => {},
    expand: () => {},
    close: () => {},
    enableClosingConfirmation: () => {},
    disableClosingConfirmation: () => {},
    onEvent: () => {},
    offEvent: () => {},
    sendData: () => {},
    openLink: () => {},
    openTelegramLink: () => {},
    openInvoice: () => {},
    showPopup: () => {},
    showAlert: () => {},
    showConfirm: () => {},
    setHeaderColor: () => {},
    setBackgroundColor: () => {}
  };
};

/**
 * Show Telegram alert
 */
export const showAlert = (message: string, callback?: () => void): void => {
  const tg = getTelegramWebApp();
  if (tg) {
    tg.showAlert(message, callback);
  } else {
    alert(message);
    callback?.();
  }
};

/**
 * Show Telegram confirm
 */
export const showConfirm = (message: string, callback?: (confirmed: boolean) => void): void => {
  const tg = getTelegramWebApp();
  if (tg) {
    tg.showConfirm(message, callback);
  } else {
    // eslint-disable-next-line no-restricted-globals
    const confirmed = confirm(message);
    callback?.(confirmed);
  }
};

/**
 * Trigger haptic feedback
 */
export const hapticFeedback = (type: 'light' | 'medium' | 'heavy' | 'success' | 'error' | 'warning' | 'selection'): void => {
  const tg = getTelegramWebApp();
  if (!tg) return;

  switch (type) {
    case 'light':
    case 'medium':
    case 'heavy':
      tg.HapticFeedback.impactOccurred(type);
      break;
    case 'success':
    case 'error':
    case 'warning':
      tg.HapticFeedback.notificationOccurred(type);
      break;
    case 'selection':
      tg.HapticFeedback.selectionChanged();
      break;
  }
};

/**
 * Get user from init data
 */
export const getTelegramUser = () => {
  const tg = getTelegramWebApp();
  return tg?.initDataUnsafe?.user || null;
};

/**
 * Get init data for backend authentication
 */
export const getInitData = (): string => {
  const tg = getTelegramWebApp();
  return tg?.initData || '';
};

/**
 * Close MiniApp
 */
export const closeMiniApp = (): void => {
  const tg = getTelegramWebApp();
  if (tg) {
    tg.close();
  }
};

/**
 * Open link in Telegram browser
 */
export const openLink = (url: string): void => {
  const tg = getTelegramWebApp();
  if (tg) {
    tg.openLink(url);
  } else {
    window.open(url, '_blank');
  }
};

/**
 * Show main button
 */
export const showMainButton = (text: string, onClick: () => void): void => {
  const tg = getTelegramWebApp();
  if (!tg) return;

  tg.MainButton.setText(text);
  tg.MainButton.onClick(onClick);
  tg.MainButton.show();
};

/**
 * Hide main button
 */
export const hideMainButton = (): void => {
  const tg = getTelegramWebApp();
  if (!tg) return;

  tg.MainButton.hide();
};

/**
 * Show back button
 */
export const showBackButton = (onClick: () => void): void => {
  const tg = getTelegramWebApp();
  if (!tg) return;

  tg.BackButton.onClick(onClick);
  tg.BackButton.show();
};

/**
 * Hide back button
 */
export const hideBackButton = (): void => {
  const tg = getTelegramWebApp();
  if (!tg) return;

  tg.BackButton.hide();
};

export default {
  getTelegramWebApp,
  initTelegramWebApp,
  showAlert,
  showConfirm,
  hapticFeedback,
  getTelegramUser,
  getInitData,
  closeMiniApp,
  openLink,
  showMainButton,
  hideMainButton,
  showBackButton,
  hideBackButton
};
